/**
 * Date and time utility functions
 *
 * Shared utilities for formatting durations and relative timestamps
 * across the application.
 */

/**
 * Format a duration in milliseconds to human-readable format
 *
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string (e.g., "2d 3h", "1h 30m", "45s")
 *
 * @example
 * formatDuration(180000) // "3m"
 * formatDuration(3600000) // "1h"
 * formatDuration(90061000) // "1d 1h"
 */
export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Format a date string to relative time
 *
 * @param dateString - ISO date string
 * @returns Relative time string (e.g., "Today", "Yesterday", "3d ago", "2w ago")
 *
 * @example
 * formatRelativeTime('2024-01-30T12:00:00Z') // "Today" (if today)
 * formatRelativeTime('2024-01-29T12:00:00Z') // "Yesterday"
 * formatRelativeTime('2024-01-25T12:00:00Z') // "5d ago"
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
